import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Session } from '../api/types';
import { SHOW_ALL_CRAWLS_TAB } from '../config/features';
import { slugify } from '../utils/urlUtils';
import { TrailIcon, ListIcon, ChartIcon } from './ui/icons';
import './BottomTabBar.css';

export interface TabItem {
  id: string;
  label: string;
  to: string;
  icon: React.ReactNode;
  isActive: (pathname: string) => boolean;
}

/**
 * The tab set shared by the bottom bar (mobile) and the app-bar nav (desktop).
 * The first slot is contextual: "My crawl" while a session is live, "Crawls"
 * when there isn't one. "All crawls" sits behind a feature toggle; the bar's
 * column count follows however many items are visible.
 */
export const useTabItems = (session: Session | null): TabItem[] => {
  const hasLiveCrawl = !!session && !session.isFinalised && !session.isComplete;
  const leaderboardTarget =
    hasLiveCrawl && session?.crawlName ? `/crawls/${slugify(session.crawlName)}` : '/crawls';

  const items: TabItem[] = [
    {
      id: 'my-crawl',
      label: session ? 'My crawl' : 'Crawls',
      to: '/',
      icon: <TrailIcon />,
      isActive: pathname => pathname === '/',
    },
  ];

  if (SHOW_ALL_CRAWLS_TAB) {
    items.push({
      id: 'all-crawls',
      label: 'All crawls',
      to: '/crawls',
      icon: <ListIcon />,
      isActive: pathname => pathname === '/crawls',
    });
  }

  items.push({
    id: 'leaderboard',
    label: 'Leaderboard',
    to: leaderboardTarget,
    icon: <ChartIcon />,
    isActive: pathname =>
      SHOW_ALL_CRAWLS_TAB ? pathname.startsWith('/crawls/') : pathname.startsWith('/crawls'),
  });

  return items;
};

interface BottomTabBarProps {
  session: Session | null;
}

const BottomTabBar: React.FC<BottomTabBarProps> = ({ session }) => {
  const items = useTabItems(session);
  const { pathname } = useLocation();

  return (
    <nav
      className="tab-bar"
      aria-label="Main"
      style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}
    >
      {items.map(item => {
        const active = item.isActive(pathname);
        return (
          <NavLink
            key={item.id}
            to={item.to}
            className={`tab-bar-item ${active ? 'tab-bar-item-active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

export default BottomTabBar;
